import React, { useState } from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  TargetedEvent,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native'

import { Href, Link } from 'zens-router'

import { Color, Radius, Spacing } from '../../style'
import { Checkbox } from '../checkbox'
import { Text } from '../typography'

import { Option } from './base'

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flex: 1,
    minHeight: 40,
    alignItems: 'center',
    paddingHorizontal: Spacing.S,
  },
  pressed: {
    backgroundColor: Color.lightGreyBlue,
  },
  hovered: {
    backgroundColor: Color.sleepingGrey,
  },
  active: {
    backgroundColor: Color.selectedBlue,
  },
  disabled: {
    backgroundColor: Color.sleepingGrey,
  },
  last: {
    borderBottomStartRadius: Radius.S,
    borderBottomEndRadius: Radius.S,
  },
  lastRound: {
    borderBottomStartRadius: Radius.L,
    borderBottomEndRadius: Radius.L,
  },
  text: {
    flexShrink: 1,
    fontSize: 14,
  },
  checkbox: {
    marginRight: Spacing.XS,
  },
})

interface SingleSelectProps {
  checkbox?: false
}

interface MultipleSelectProps {
  checkbox: true
  selected?: boolean
}

interface CommonProps extends Omit<PressableProps, 'style'> {
  value?: Option<any>
  last?: boolean
  round?: boolean
  style?: StyleProp<ViewStyle>
  textStyle?: StyleProp<TextStyle>
  checkbox?: boolean
  checkboxStyle?: StyleProp<ViewStyle>
  href?: Href
}

export type Props = (SingleSelectProps | MultipleSelectProps) & CommonProps

const SelectOption = (props: Props) => {
  const {
    value,
    last,
    round,
    disabled,
    onFocus,
    onBlur,
    style,
    textStyle,
    checkbox,
    checkboxStyle,
    href,
    ...otherProps
  } = props
  const selected = checkbox && (props as MultipleSelectProps).selected
  const [active, setActive] = useState(false)

  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TargetedEvent>) => {
    setActive(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }

  const renderContent = (state: { pressed: boolean; hovered?: boolean }) => {
    const { pressed, hovered } = state

    const containerStyle = [
      styles.container,
      hovered && styles.hovered,
      active && styles.active,
      pressed && styles.pressed,
      (disabled || value?.disabled) && styles.disabled,
      last && styles.last,
      last && round && styles.lastRound,
      style,
      value?.style,
      hovered && value?.hoveredStyle,
    ]
    // const outlineStyle = [
    //   active && styles.active,
    //   last && styles.lastOutline,
    //   round && last && styles.lastOutlineRound,
    // ];
    return (
      <View style={containerStyle}>
        {checkbox && <Checkbox style={styles.checkbox} boxStyle={checkboxStyle} disabled value={selected} />}
        <Text style={[styles.text, textStyle]} numberOfLines={1}>
          {value?.render ? value?.render(value) : value?.title || value?.value}
        </Text>
      </View>
    )
  }
  const option = (
    <Pressable
      disabled={disabled || value?.disabled}
      onFocus={updateActive(true)}
      onBlur={updateActive(false)}
      style={Platform.select({
        // @ts-expect-error outline style none is not defined
        web: {
          outlineStyle: 'none',
        } as ViewStyle,
      })}
      testID="Option.Container"
      {...otherProps}
    >
      {renderContent}
    </Pressable>
  )

  return href ? (
    <Link href={href} asChild>
      {option}
    </Link>
  ) : (
    option
  )
}

export default SelectOption
